import { useState, useEffect, useCallback } from 'react';
import type { InvoiceScheduleListItem } from '@techgitter/contracts';
import { getCustomerInvoiceSchedules } from '../services/invoiceScheduleAdminService';

export interface UseCustomerInvoiceSchedulesResult {
  schedules: InvoiceScheduleListItem[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Load a customer's invoice schedules (read-only) for the customer detail page.
 * @param dataverseCustomerId the Dataverse customer GUID (cr001_customersid).
 */
export function useCustomerInvoiceSchedules(
  dataverseCustomerId: string | null,
): UseCustomerInvoiceSchedulesResult {
  const [schedules, setSchedules] = useState<InvoiceScheduleListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (signal?: AbortSignal) => {
    if (!dataverseCustomerId) {
      setSchedules([]);
      setError(null);
      return;
    }
    setIsLoading(true);
    setError(null);
    try {
      const result = await getCustomerInvoiceSchedules(dataverseCustomerId, signal);
      if (signal?.aborted) return;
      setSchedules(result);
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load invoice schedules');
      setSchedules([]);
    } finally {
      if (!signal?.aborted) setIsLoading(false);
    }
  }, [dataverseCustomerId]);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const refresh = useCallback(() => {
    load();
  }, [load]);

  return { schedules, isLoading, error, refresh };
}
